/* istanbul ignore file */
// TODO add unit tests for this component and remove the line above to include this file in coverage calculation
import React from 'react';

import { HorizontalWrapper } from 'moet-core';
import { SpacerHorizontal } from '@klarna/bubble-ui';

import { CallToActionButton, ContainerTemplate, SecondaryButton } from '@/components';

import { OnboardingGroupComponentProps } from '@/bpc/components/OnboardingGroupComponent/OnboardingGroupComponent';
import { CardsSelector } from '@/bpc/components/CardsSelector';
import { SensitiveGoodCategory } from '@mjolnir/mf-fields-ts/src/enm/SensitiveGoodCategory';
import { findFieldByAbsolutePath } from '@/bpc/utils/onboardingGroup';
import { validateHasSensitiveGoods } from '@/bpc/utils/sensitiveGoods';
import { logEvent } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/bpc/utils/analyticsEvents';

export interface SensitiveGoodsGroupComponentState {
  highlightMandatoryButEmptyFields: boolean;
  isDisabled: boolean;
  errorMessage: string | undefined;
  isRequiredErrorMessage: string | undefined;
  validatorErrorMessage: string | undefined;
  value: string;
}

const SENSITIVE_GOODS_FIELD_PATH = 'sensitive_goods';

export class SensitiveGoodsGroupComponent extends React.Component<
  OnboardingGroupComponentProps,
  SensitiveGoodsGroupComponentState
> {
  constructor(props: OnboardingGroupComponentProps) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.onNext = this.onNext.bind(this);
    this.validateIsRequired = this.validateIsRequired.bind(this);
    this.preventNavigation = this.preventNavigation.bind(this);
    this.setSensitiveGoodsErrorMessage = this.setSensitiveGoodsErrorMessage.bind(this);
    this.setRequiredErrorMessage = this.setRequiredErrorMessage.bind(this);
    this.setValidatorErrorMessage = this.setValidatorErrorMessage.bind(this);

    this.state = {
      highlightMandatoryButEmptyFields: false,
      isDisabled: false,
      errorMessage: undefined,
      isRequiredErrorMessage: undefined,
      validatorErrorMessage: undefined,
      value: this.props.group.getValueForField(
        this.props.group.fields[0][0].absolutePath,
      ) as string,
    };
  }

  setSensitiveGoodsErrorMessage(relatedErrorMessage: string): void {
    const { group } = this.props;
    this.setState({
      value: group.getValueForField(SENSITIVE_GOODS_FIELD_PATH) as SensitiveGoodCategory,
      errorMessage: relatedErrorMessage,
    });
  }

  setValidatorErrorMessage(): void {
    const { group } = this.props;
    const field = findFieldByAbsolutePath(group, SENSITIVE_GOODS_FIELD_PATH);
    const value = group.getValueForField(SENSITIVE_GOODS_FIELD_PATH) as SensitiveGoodCategory;
    const validator = field.validator;

    this.setState({
      value,
      errorMessage: validator && !validator.callback(value) ? validator.errorMessage() : undefined,
    });
  }

  setRequiredErrorMessage(): void {
    const { t, group } = this.props;
    const field = findFieldByAbsolutePath(group, SENSITIVE_GOODS_FIELD_PATH);
    const label = t(field.label) || field.fallbackLabel;
    this.setState({
      isRequiredErrorMessage: `${label} ${this.props.t('products-fe.errors.mandatoryField')}`,
    });
  }

  onChange(value: SensitiveGoodCategory): void {
    const group = this.props.group;
    const field = findFieldByAbsolutePath(group, SENSITIVE_GOODS_FIELD_PATH);

    group.setValueOfField(field, value);
    this.setState({
      value: group.getValueForField(SENSITIVE_GOODS_FIELD_PATH) as SensitiveGoodCategory,
      isRequiredErrorMessage: undefined,
      errorMessage: undefined,
      highlightMandatoryButEmptyFields: false,
    });
    logEvent(ANALYTICS_EVENTS.submitSensitiveGoods, {
      sellsSensitiveGoods: group.getValueForField(
        SENSITIVE_GOODS_FIELD_PATH,
      ) as SensitiveGoodCategory,
    });
  }

  validateIsRequired(): boolean {
    const { group } = this.props;
    const field = findFieldByAbsolutePath(group, SENSITIVE_GOODS_FIELD_PATH);
    const value = group.getValueForField(field.absolutePath);
    const isMandatory = field.mandatory();
    const isApplicable = field.applicable();

    const isRequired =
      isApplicable && isMandatory && (value == null || value === '' || value === undefined);

    if (isRequired) {
      this.setState({
        highlightMandatoryButEmptyFields: true,
      });
      this.setRequiredErrorMessage();
    }

    return isRequired;
  }

  preventNavigation(): boolean {
    const { t, group } = this.props;

    const isRequired = this.validateIsRequired();
    const hasSensitiveGood = validateHasSensitiveGoods(group);
    const shouldPreventNavigation = isRequired || hasSensitiveGood;

    if (shouldPreventNavigation) {
      if (hasSensitiveGood) {
        this.setSensitiveGoodsErrorMessage(t('fields.enm.sensitive_good_category.error_message'));
      } else if (isRequired) {
        const field = findFieldByAbsolutePath(group, SENSITIVE_GOODS_FIELD_PATH);
        const label = t(field.label) || field.fallbackLabel;
        this.setSensitiveGoodsErrorMessage(`${label} ${t('products-fe.errors.mandatoryField')}`);
      }
    }

    return shouldPreventNavigation;
  }

  onNext(): void {
    const { hasNextGroup, onNextGroup, onShowSummaryPage } = this.props;

    if (this.preventNavigation()) {
      return;
    }

    if (hasNextGroup) {
      onNextGroup();
    } else {
      onShowSummaryPage();
    }
  }

  render(): React.ReactElement {
    const { group, error, hasPreviousGroup, onPreviousGroup, isLoading, t } = this.props;
    const field = group.fields[0][0];

    return (
      <ContainerTemplate
        title={t('group.title.sensitive_goods') || group.fallbackTitle}
        subtitle={t('group.subtitle.sensitive_goods') || group.fallbackSubtitle}
        error={error}
      >
        <CardsSelector
          value={group.getValueForField(field.absolutePath) as SensitiveGoodCategory}
          onChange={this.onChange}
          options={field?.enumOptions()}
          error={this.state.errorMessage}
          showErrorOnTop={!this.state.highlightMandatoryButEmptyFields}
        />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasPreviousGroup && (
            <>
              <SecondaryButton onClick={onPreviousGroup}>
                {t('products-fe.general.buttons.back')}
              </SecondaryButton>
              <SpacerHorizontal spaceToken="space/300" />
            </>
          )}
          <CallToActionButton onClick={this.onNext} loading={isLoading}>
            {t('products-fe.general.buttons.continue')}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}
